import React, { useCallback, useEffect, useState } from "react";
import axios from "../../api";
import {
  Container,
  Paper,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Grid,
} from "@mui/material";
import { Business, Add, PersonAdd } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import AdminLayout from "../AdminLayout";
import { currentRole, ROLES } from "../../utils/roles";

function Organizations({ onLogout }) {
  const navigate = useNavigate();
  const role = currentRole();
  const canCreate = ROLES.SUPER.includes(role);
  const [loading, setLoading] = useState(true);
  const [orgs, setOrgs] = useState([]);
  const [form, setForm] = useState({ name: "", slug: "", legalName: "" });
  const [saving, setSaving] = useState(false);
  const [assignOrgId, setAssignOrgId] = useState("");
  const [assignEmails, setAssignEmails] = useState("");
  const [assigning, setAssigning] = useState(false);

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/organizations");
      setOrgs(res.data.result || []);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to load organizations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  const handleLogout = () => {
    if (onLogout) return onLogout();
    localStorage.clear();
    navigate("/login");
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    if (!name) return toast.warning("Organization name is required");
    setSaving(true);
    try {
      const payload = { name };
      if (form.slug.trim()) payload.slug = form.slug.trim();
      if (form.legalName.trim()) payload.legalName = form.legalName.trim();
      await axios.post("/organizations", payload);
      toast.success("Organization created");
      setForm({ name: "", slug: "", legalName: "" });
      fetchOrgs();
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.error || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!canCreate) return;
    if (!assignOrgId) return toast.warning("Select an organization");
    const emails = assignEmails
      .split(/[\s,;]+/)
      .map((s) => s.trim().toLowerCase())
      .filter((s) => s.includes("@"));
    if (!emails.length) return toast.warning("Enter at least one user email");
    setAssigning(true);
    try {
      const res = await axios.put(`/organizations/${assignOrgId}/assign`, { emails });
      toast.success(res.data.message || "Users assigned");
      setAssignEmails("");
    } catch (err) {
      toast.error(err.response?.data?.message || "Assign failed");
    } finally {
      setAssigning(false);
    }
  };

  return (
    <AdminLayout title="Organizations" subtitle="Tenant organizations" onLogout={handleLogout}>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Settings (branding, email templates) remain global for this deployment. Organizations
          scope users and assets for multi-company data separation.
        </Alert>

        {canCreate && (
          <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
              <Add fontSize="small" /> Create organization
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Super admins only. Slug is auto-generated from the name when left blank.
            </Typography>
            <Box component="form" onSubmit={handleCreate}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Name"
                    fullWidth
                    size="small"
                    required
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Slug (optional)"
                    fullWidth
                    size="small"
                    value={form.slug}
                    onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                    helperText="URL-safe identifier"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Legal name (optional)"
                    fullWidth
                    size="small"
                    value={form.legalName}
                    onChange={(e) => setForm((f) => ({ ...f, legalName: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button type="submit" variant="contained" disabled={saving}>
                    {saving ? "Creating…" : "Create"}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        )}

        {canCreate && (
          <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
              <PersonAdd fontSize="small" /> Assign users by email
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Attach existing accounts to an organization (comma or newline separated emails).
            </Typography>
            <Box component="form" onSubmit={handleAssign}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    SelectProps={{ native: true }}
                    label="Organization"
                    fullWidth
                    size="small"
                    value={assignOrgId}
                    onChange={(e) => setAssignOrgId(e.target.value)}
                    required
                  >
                    <option value="">Select…</option>
                    {orgs.map((o) => (
                      <option key={o._id} value={o._id}>
                        {o.name}
                      </option>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    label="User emails"
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    value={assignEmails}
                    onChange={(e) => setAssignEmails(e.target.value)}
                    placeholder="alice@company.com, bob@company.com"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button type="submit" variant="outlined" disabled={assigning}>
                    {assigning ? "Assigning…" : "Assign users"}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        )}

        <Paper variant="outlined">
          <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <Business color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Organizations ({orgs.length})
            </Typography>
          </Box>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : orgs.length === 0 ? (
            <Alert severity="info" sx={{ m: 2 }}>
              No organizations yet.
            </Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Slug</TableCell>
                  <TableCell>Legal name</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orgs.map((o) => (
                  <TableRow key={o._id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{o.name}</TableCell>
                    <TableCell>{o.slug || "—"}</TableCell>
                    <TableCell>{o.legalName || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={o.status || "Active"}
                        color={o.status === "Inactive" ? "default" : "success"}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Container>
      <ToastContainer position="top-center" autoClose={2500} />
    </AdminLayout>
  );
}

export default Organizations;
